// noinspection JSUnusedGlobalSymbols

import type {MediaObject, IUserData} from "./";
import {AccountTypes} from "../constants";

export enum AssetTypes {
  Book = "book",
  Document = "document",
  Article = "article",
  Presentation = "presentation",
  Video = "video"
}

export interface IAssetFilter {
  assetType?: AssetTypes;
  label?: string;
  default?: boolean;
}

export const AssetFilters: IAssetFilter[] = [
  {
    assetType: AssetTypes.Book,
    label: "Books",
    default: true
  },
  {
    assetType: AssetTypes.Document,
    label: "Documents"
  },
  {
    assetType: AssetTypes.Article,
    label: "Articles"
  },
  {
    assetType: AssetTypes.Presentation,
    label: "Presentations"
  },
  {
    assetType: AssetTypes.Video,
    label: "Videos"
  }
];

export enum AssetSortFields {
  title = "title",
  author = "author",
  publisher = "publisher",
  publishDate = "publishDate",
  ratings = "analytics.ratings.overall",
  reviews = "analytics.reviews.total",
  reactions = "analytics.reactions.total",
  shares = "analytics.shares.total"
}

export enum AssetSortLabels {
  title = "Title",
  author = "Author",
  publisher = "Publisher",
  publishDate = "Publish Date",
  ratings = "Rating",
  reviews = "Reviews",
  reactions = "Reactions",
  shares = "Shares"
}

export enum AssetSortDirections {
  asc = "asc",
  desc = "desc"
}

export enum AssetSortIconTypes {
  alphabetical = "alphabetical",
  numeric = "numeric"
}

export interface IAssetSort {
  field: AssetSortFields;
  label: AssetSortLabels;
  direction: AssetSortDirections;
  iconType: AssetSortIconTypes;
  default?: boolean;
}

export const AssetSorts: IAssetSort[] = [
  {
    field: AssetSortFields.title,
    label: AssetSortLabels.title,
    direction: AssetSortDirections.asc,
    iconType: AssetSortIconTypes.alphabetical
  },
  {
    field: AssetSortFields.author,
    label: AssetSortLabels.author,
    direction: AssetSortDirections.asc,
    iconType: AssetSortIconTypes.alphabetical
  },
  {
    field: AssetSortFields.publisher,
    label: AssetSortLabels.publisher,
    direction: AssetSortDirections.asc,
    iconType: AssetSortIconTypes.alphabetical
  },
  {
    field: AssetSortFields.publishDate,
    label: AssetSortLabels.publishDate,
    direction: AssetSortDirections.desc,
    iconType: AssetSortIconTypes.alphabetical,
    default: true
  },
  {
    field: AssetSortFields.ratings,
    label: AssetSortLabels.ratings,
    direction: AssetSortDirections.asc,
    iconType: AssetSortIconTypes.numeric
  },
  {
    field: AssetSortFields.reviews,
    label: AssetSortLabels.reviews,
    direction: AssetSortDirections.asc,
    iconType: AssetSortIconTypes.numeric
  },
  {
    field: AssetSortFields.reactions,
    label: AssetSortLabels.reactions,
    direction: AssetSortDirections.asc,
    iconType: AssetSortIconTypes.numeric
  },
  {
    field: AssetSortFields.shares,
    label: AssetSortLabels.shares,
    direction: AssetSortDirections.asc,
    iconType: AssetSortIconTypes.numeric
  }
];

export enum AssetReactions {
  love = "love",
  thumbsUp = "thumbsUp",
  agree = "agree",
  clap = "clap",
  rocks = "rocks",
  support = "support",
  insightful = "insightful",
  enlightening = "enlightening",
  hundredPercent = "hundredPercent",
  hate = "hate",
  thumbsDown = "thumbsDown",
  disagree = "disagree",
  boring = "boring",
  heartBreaking = "heartBreaking",
  disappointed = "disappointed"
}

export enum AssetReactionIcons {
  love = "fa-heart",
  thumbsUp = "fa-thumbs-up",
  agree = "fa-circle-check",
  clap = "fa-hands-clapping",
  rocks = "fa-hand-horns",
  support = "fa-handshake-angle",
  insightful = "fa-eyes",
  enlightening = "fa-lightbulb-on",
  hundredPercent = "fa-hundred-points",
  hate = "fa-heart-slash",
  thumbsDown = "fa-thumbs-down",
  disagree = "fa-circle-xmark",
  boring = "fa-face-hand-yawn",
  heartBreaking = "fa-heart-crack",
  disappointed = "fa-face-disappointed"
}

export enum AssetReactionCaptions {
  love = "Love",
  thumbsUp = "Like",
  agree = "Agree",
  clap = "Clap",
  rocks = "Rocks",
  support = "Support",
  insightful = "Insightful",
  enlightening = "Enlightening",
  hundredPercent = "100%",
  hate = "Hate",
  thumbsDown = "Dislike",
  disagree = "Disagree",
  boring = "Boring",
  heartBreaking = "Heart Breaking",
  disappointed = "Disappointed"
}

export enum AssetReactionOrder {
  love = 1,
  thumbsUp = 2,
  agree = 3,
  clap = 4,
  rocks = 5,
  support = 6,
  insightful = 7,
  enlightening = 8,
  hundredPercent = 9,
  hate = 10,
  thumbsDown = 11,
  disagree = 12,
  boring = 13,
  heartBreaking = 14,
  disappointed = 15
}

export enum AssetSharePlatforms {
  facebook = "facebook",
  twitter = "twitter",
  linkedin = "linkedin",
  whatsapp = "whatsapp",
  email = "email",
  copyLink = "copyLink"
}

export enum AssetShareIcons {
  linkedin = "fa-brands fa-linkedin",
  twitter = "fa-brands fa-x-twitter",
  facebook = "fa-brands fa-square-facebook",
  whatsapp = "fa-brands fa-square-whatsapp",
  email = "fa-duotone fa-light fa-envelope",
  copyLink = "fa-duotone fa-light fa-link"
}

export interface IBookMark {
  pageNumber: number;
  pageTitle?: string;
}

export interface IBookmarks {
  user: IUserData;
  bookmarks: IBookMark[];
}

export interface IRating {
  user: IUserData;
  rating: number;
  dateTime: string;
}

export interface IReview {
  reviewId: string;
  user: IUserData;
  review: string;
  dateTime: string;
}

export interface IReply {
  reviewId: string;
  parentReplyId?: string;
  replyId: string;
  user: IUserData;
  reply: string;
  dateTime: string;
}

export interface IReaction {
  user: IUserData;
  reaction: AssetReactions;
  dateTime: string;
}

export interface IShare {
  user: IUserData;
  platform: AssetSharePlatforms;
  assetUrl: string;
  dateTime: string;
}

export interface IAssetFeed {
  id: string;
  bookmarks?: IBookmarks[];
  ratings?: IRating[];
  reviews?: IReview[];
  replies?: IReply[];
  reactions?: IReaction[];
  shares?: IShare[];
}

export interface IAssetRatingSummary {
  rating: number;
  percentage: number;
  total: number;
}

export interface IAssetRatings {
  overall: number;
  total: number;
  summary: IAssetRatingSummary[];
}

export interface IAssetReviews {
  total: number;
}

export interface IAssetReactionSummary {
  reaction: AssetReactions;
  percentage: number;
  total: number;
}

export interface IAssetReactions {
  total: number;
  summary: IAssetReactionSummary[]
}

export interface IAssetShareSummary {
  platform: AssetSharePlatforms;
  percentage: number;
  total: number;
}

export interface IAssetShares {
  total: number;
  summary: IAssetShareSummary[]
}

export interface IAssetAnalytics {
  ratings?: IAssetRatings;
  reviews?: IAssetReviews;
  reactions?: IAssetReactions;
  shares?: IAssetShares;
}

export interface IPage {
  pageNumber?: number;
  page: MediaObject;
  cover?: boolean;
  backCover?: boolean;
}

export interface IAsset {
  id: string;
  assetId: string;
  assetType: AssetTypes;
  showInAtheneum: boolean;
  title: string;
  author: string;
  socialID: string;
  publisher?: string;
  publishedDate?: string;
  description: string;
  roleAccess: Array<AccountTypes>;
  metadata?: {
    width: number;
    height: number;
    aspectRatio: number;
  };
  pages?: Array<IPage>;
  totalPages: number;
  video?: MediaObject;
  analytics?: IAssetAnalytics;
}

